import * as THREE from 'three';
import type { Anim } from './anim';
import { glowMaterial, translucentSource } from './materials';

/**
 * Baking: a model is mounted once per look (type, level, variant) with the
 * placeholder `BAKE_ACCENT`/`BAKE_GLOW` materials, and its Object3D tree is
 * flattened into parts here. The registry stays JSX; restyling a model is
 * still editing its JSX.
 */

/** Which material a part draws with: a shared palette material, or the owner's colour. */
export type Slot =
  | { kind: 'shared'; material: THREE.Material }
  /** The owner's flat colour; `opacity` for a `translucent` copy of it. */
  | { kind: 'accent'; opacity?: number }
  /** The owner's emissive colour; `opacity` for a `translucent` copy of it. */
  | { kind: 'glow'; opacity?: number };

export interface Part {
  /** Animated parts: already moved into their animated group's frame. */
  geometry: THREE.BufferGeometry;
  slot: Slot;
  /** Part (animated parts: their animated group) → model root. */
  matrix: THREE.Matrix4;
  /** How the animated group moves, about its origin (see `anim.ts`). */
  anim?: Anim;
}

/** Placeholder materials a look is mounted with for baking; never drawn. */
export const BAKE_ACCENT = new THREE.MeshStandardMaterial({ color: '#ffffff', flatShading: true });
export const BAKE_GLOW = glowMaterial('#ffffff');

// Animated parts' geometry moved into the animated group's frame, one copy per
// (geometry, offset): every drone's rotor blades share two copies.
const movedGeometry = new Map<string, THREE.BufferGeometry>();

function moved(geometry: THREE.BufferGeometry, offset: THREE.Matrix4): THREE.BufferGeometry {
  if (offset.equals(IDENTITY)) return geometry;
  const key = `${geometry.uuid}|${offset.elements.map((e) => e.toFixed(6)).join(',')}`;
  let copy = movedGeometry.get(key);
  if (!copy) {
    copy = geometry.clone().applyMatrix4(offset);
    movedGeometry.set(key, copy);
  }
  return copy;
}

const IDENTITY = new THREE.Matrix4();

function slotOf(material: THREE.Material): Slot {
  if (material === BAKE_ACCENT) return { kind: 'accent' };
  if (material === BAKE_GLOW) return { kind: 'glow' };
  const see = translucentSource(material);
  if (see?.source === BAKE_ACCENT) return { kind: 'accent', opacity: see.opacity };
  if (see?.source === BAKE_GLOW) return { kind: 'glow', opacity: see.opacity };
  return { kind: 'shared', material };
}

/**
 * Flatten a mounted model into parts, relative to `root`. A part under an
 * animated group (`userData.anim`, from the `animated.tsx` wrappers) takes
 * that group's motion; animations don't nest (the innermost wins), so a model
 * gives a part that moves two ways one wrapper carrying both.
 */
export function bakeParts(root: THREE.Object3D): Part[] {
  root.updateMatrixWorld(true);
  const rootInv = root.matrixWorld.clone().invert();
  const parts: Part[] = [];
  root.traverseVisible((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    if (Array.isArray(mesh.material)) {
      console.warn('bakeParts: multi-material meshes are not batched', mesh);
      return;
    }
    const slot = slotOf(mesh.material);
    let animated: THREE.Object3D | null = null;
    for (let p: THREE.Object3D | null = mesh; p && p !== root; p = p.parent) {
      if (!p.userData.anim) continue;
      if (animated) console.warn('bakeParts: nested animations; the innermost wins', p);
      else animated = p;
    }
    if (!animated) {
      parts.push({
        geometry: mesh.geometry,
        slot,
        matrix: rootInv.clone().multiply(mesh.matrixWorld),
      });
      return;
    }
    const offset = animated.matrixWorld.clone().invert().multiply(mesh.matrixWorld);
    parts.push({
      geometry: moved(mesh.geometry, offset),
      slot,
      matrix: rootInv.clone().multiply(animated.matrixWorld),
      anim: animated.userData.anim as Anim,
    });
  });
  return parts;
}
